import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  addQueryWithReplace, QueryVal, removeQuery,
} from 'Helpers/Query';
import fr from 'facet-rest';
import moment from 'moment-timezone';
import { dispatchMeetingBooked } from 'Hooks/useRefreshEvent';

const ClientSchedulerStateContext = React.createContext(undefined);
const ClientSchedulerDispatchContext = React.createContext(undefined);

const adjustTimeZone = (link) => {
  const url = new URL(link);
  const params = new URLSearchParams(url.search);
  const urlTZ = params.get('tz');
  if (!urlTZ) {
    return url.toString();
  }
  const tz = moment.tz.guess();
  if (urlTZ !== tz) {
    const startParam = params.get('start');
    const endParam = params.get('end');
    const startTimeInOriginalTimeZone = moment(startParam).tz(urlTZ).format('YYYY-MM-DDTHH:MM:SS');
    const startTimeInLocalTimeZone = `${moment(startTimeInOriginalTimeZone).tz(tz).toISOString().split('.')[0]}Z`;
    const endTimeInOriginalTimeZone = moment(endParam).tz(urlTZ).format('YYYY-MM-DDTHH:MM:SS');
    const endTimeInLocalTimeZone = `${moment(endTimeInOriginalTimeZone).tz(tz).toISOString().split('.')[0]}Z`;
    params.set('start', startTimeInLocalTimeZone);
    params.set('end', endTimeInLocalTimeZone);
    params.set('tz', tz);
    url.search = params.toString();
  }
  return url.toString();
};

const fetchDataFromLinkQueryParam = async (dispatch) => {
  dispatch({ type: 'loadingStart' });
  // Backwards compatibility checks
  if (QueryVal('availabilityPeriodLink')) {
    addQueryWithReplace({ link: QueryVal('availabilityPeriodLink') });
    removeQuery('availabilityPeriodLink');
  }

  if (QueryVal('rescheduleEventLink')) {
    const rescheduleCalendarEventLink = QueryVal('rescheduleEventLink');
    // fetch calendar event to reschedule
    const [calendarEventToReschedule] = await fr.http.get(rescheduleCalendarEventLink);
    const [meetingToReschedule] = await calendarEventToReschedule.relationship(fr.relationships.meetings.name).link('related').get({
      params: {
        tz: moment.tz.guess(),
      },
    });
    addQueryWithReplace({ link: meetingToReschedule.relationship(fr.relationships.rescheduleMeeting.name).link('related').url() });
    removeQuery('rescheduleEventLink');
  }


  try {
    const link = QueryVal('link');
    const adjustedLink = adjustTimeZone(link);
    const fetchedCollection = await fr.http.get(adjustedLink);
    const type = fetchedCollection.meta().type();
    switch (type) {
      case 'availabilityPeriod': {
        const availabilityPeriodResource = fetchedCollection.resources().head();
        const isExpired = availabilityPeriodResource.attr('isExpired');

        // Check if this is a ChiliPiper meeting based on server response
        const isChilipiperMeeting = availabilityPeriodResource.attr('isChilipiperMeeting') === 'true';

        // Check if this has a ChiliPiper relationship
        const hasChiliPiperRelationship = availabilityPeriodResource.withRelationship(
          fr.relationships.chilipiperRescheduleLink.name,
          (rel) => rel.meta('isChiliPiperLink'),
          () => false,
        );

        if (isChilipiperMeeting && hasChiliPiperRelationship) {
          // Auto-trigger ChiliPiper dialog by setting a special view
          dispatch({ type: 'setView', view: 'autoChiliPiper', newCollection: fetchedCollection });
        } else if (isChilipiperMeeting && !hasChiliPiperRelationship) {
          // ChiliPiper meeting without reschedule link - show unable to schedule
          dispatch({ type: 'setView', view: 'unableToSchedule', newCollection: fetchedCollection });
        } else if (isExpired === 'true') {
          dispatch({ type: 'setView', view: 'meetingExpired', newCollection: fetchedCollection });
        } else {
          dispatch({ type: 'setView', view: 'month', newCollection: fetchedCollection });
        }
        break;
      }
      case 'calendarEvent': {
        const meetingBooked = QueryVal('meetingBooked');
        if (meetingBooked === 'true') {
          dispatch({ type: 'setView', view: 'meetingBooked', newCollection: fetchedCollection });
        } else {
          dispatch({ type: 'setView', view: 'confirmTime', newCollection: fetchedCollection });
        }
        break;
      }
      default:
        dispatch({ type: 'setView', view: null, newCollection: null });
    }
  } catch (e) {
    dispatch({ type: 'setResponseError', responseError: e.errors });
  } finally {
    dispatch({ type: 'loadingEnd' });
  }
};

const setNewLinkQueryParam = (dispatch, link) => {
  addQueryWithReplace({ link });
};

function init() {
  return {
    view: null,
    currentCollection: null,
    loading: false,
    responseError: null,
    selectedMeetingStart: QueryVal('selectedMeetingStart'),
    selectedMeetingEnd: QueryVal('selectedMeetingEnd'),
    bufferDuration: QueryVal('bufferDuration'),
    prevLink: QueryVal('prevLink'),
    meetingBooked: QueryVal('meetingBooked'),
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'setResponseError': {
      return {
        ...state,
        responseError: action.responseError,
        view: 'error',
      };
    }
    case 'setView': {
      return {
        ...state,
        view: action.view,
        currentCollection: action.newCollection,
      };
    }
    case 'loadingStart': {
      return {
        ...state,
        loading: true,
        view: null,
      };
    }
    case 'loadingEnd': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'setConfirmTime': {
      const {
        selectedMeetingStart,
        selectedMeetingEnd,
        bufferDuration,
        eventType,
      } = action;

      addQueryWithReplace(
        { selectedMeetingStart },
        { selectedMeetingEnd },
        { bufferDuration },
        { eventType },
      );

      return {
        ...state,
        selectedMeetingStart,
        selectedMeetingEnd,
        bufferDuration,
        eventType,
      };
    }
    case 'setPrevLink': {
      const { prevLink } = action;
      addQueryWithReplace({ prevLink });
      return {
        ...state,
        prevLink,
      };
    }
    case 'setMeetingBooked': {
      dispatchMeetingBooked();
      addQueryWithReplace({ meetingBooked: true });
      return {
        ...state,
        meetingBooked: true,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}
function ClientSchedulerProvider({ children }) {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  useEffect(() => {
    fetchDataFromLinkQueryParam(dispatch);
  }, []);

  return (
    <ClientSchedulerStateContext.Provider value={state}>
      <ClientSchedulerDispatchContext.Provider value={dispatch}>
        {children}
      </ClientSchedulerDispatchContext.Provider>
    </ClientSchedulerStateContext.Provider>
  );
}

function useClientSchedulerStateContext() {
  const context = React.useContext(ClientSchedulerStateContext);

  if (context === undefined) {
    throw new Error('useClientSchedulerStateContext must be used within a ClientSchedulerProvider');
  }

  return context;
}

function useClientSchedulerDispatchContext() {
  const context = React.useContext(ClientSchedulerDispatchContext);

  if (context === undefined) {
    throw new Error('useClientSchedulerDispatchContext must be used within a ClientSchedulerProvider');
  }

  return context;
}

const useClientSchedulerContext = () => {
  const dispatch = React.useContext(ClientSchedulerDispatchContext);
  const state = React.useContext(ClientSchedulerStateContext);

  if (!dispatch) {
    throw new Error('useClientSchedulerContext must be called from ClientSchedulerProvider');
  }

  if (!state) {
    throw new Error('useClientSchedulerContext must be called from ClientSchedulerProvider');
  }

  return { dispatch, state };
};

ClientSchedulerProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

const closeSchedulerModal = () => {
  removeQuery(
    'modal',
    'link',
    'selectedMeetingStart',
    'selectedMeetingEnd',
    'bufferDuration',
    'eventType',
    'prevLink',
    'meetingBooked',
  );
};

export {
  ClientSchedulerProvider,
  useClientSchedulerContext,
  useClientSchedulerStateContext,
  useClientSchedulerDispatchContext,
  closeSchedulerModal,
  fetchDataFromLinkQueryParam,
  setNewLinkQueryParam,
};
